import React, { useEffect, useState, useMemo } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import UsedProgress from "../../../components/usedProgress";
import FreeSubscription from "./FreeSubscription";
import Warning from "./warnings/InfoWarning";
import { ExclamationTriangle, ExclamationCircle } from "react-bootstrap-icons";
import { usePaymentPlansQuery } from "../../../graphql/payment.hooks";
import PaidSubscription from "./PaidSubscription";
import { isPaymentError, selectedCurrency } from "../../../graphql/state";
import { gql, useQuery, useReactiveVar } from "@apollo/client";
import { useUserQuery } from "../../../graphql/menu.hooks";

const PAYMENT_SUBSCRIPTION_ACCESS_QUERY = gql`
  query PaymentSubscriptionAccess {
    subscriptionAccess {
      paidOnly
      trialActive
      trialEndsAt
    }
  }
`;

type PaymentSubscriptionAccessQuery = {
  subscriptionAccess: {
    paidOnly: boolean;
    trialActive: boolean;
    trialEndsAt?: string | null;
  };
};

export enum WarningStatus {
  Info = "info",
  Danger = "danger",
}

export default function SubscriptionInfo() {
  const { t } = useTranslation();

  const [showAlert, setShowAlert] = useState<boolean>(false);

  const currentCurrency = useReactiveVar(selectedCurrency);
  const { data, loading, error } = usePaymentPlansQuery({
    variables: { locale: "en", currency: currentCurrency },
    fetchPolicy: "network-only",
  });
  const { data: userData } = useUserQuery({
    fetchPolicy: "network-only",
  });
  const { data: subscriptionAccessData, loading: subscriptionAccessLoading } =
    useQuery<PaymentSubscriptionAccessQuery>(
      PAYMENT_SUBSCRIPTION_ACCESS_QUERY,
      { fetchPolicy: "network-only" }
    );

  const usedPublishPages = userData?.user?.countPublishPages;
  const limitUsedPublishPages = 5;

  const usedPublishPagesPercent = useMemo(
    () => (usedPublishPages * 100) / limitUsedPublishPages,
    [usedPublishPages, limitUsedPublishPages]
  );

  const showWarningPublishPagesStatus = useMemo(() => {
    if (usedPublishPagesPercent > 90) return WarningStatus.Danger;
    if (usedPublishPagesPercent > 80) return WarningStatus.Info;
    return null;
  }, [usedPublishPagesPercent]);

  useEffect(() => {
    setShowAlert(usedPublishPagesPercent > 75);
  }, [usedPublishPagesPercent]);

  useEffect(() => {
    if (!error) return;
    isPaymentError(true);
  }, [error]);

  const getWarningIcon = (showWarningStatus: string) =>
    showWarningStatus === WarningStatus.Danger ? (
      <ExclamationTriangle />
    ) : (
      <ExclamationCircle />
    );

  const getWarningColor = (showWarningStatus: string) =>
    showWarningStatus === WarningStatus.Danger ? "danger" : "warning";

  if (loading || subscriptionAccessLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  const subscriptionAccess = subscriptionAccessData?.subscriptionAccess;

  const currencySymbol = data?.paymentPlans.currencies.find(
    (item) => item.currencyName === currentCurrency
  ).currencySymbol;

  if (data?.paymentPlans?.activePremium || subscriptionAccess?.paidOnly) {
    return (
      <PaidSubscription
        activePremium={data?.paymentPlans?.activePremium}
        premiumOnly={subscriptionAccess?.paidOnly}
        trialActive={subscriptionAccess?.trialActive}
        trialEndsAt={subscriptionAccess?.trialEndsAt}
      />
    );
  }

  return (
    <>
      <FreeSubscription currencySymbol={currencySymbol} />
      {showAlert && (
        <Warning
          closeAlert={() => setShowAlert(false)}
          icon={getWarningIcon(showWarningPublishPagesStatus)}
          color={getWarningColor(showWarningPublishPagesStatus)}
        />
      )}
      <Row className="flex-column gy-4 mt-2">
        <Col>
          <UsedProgress
            title={t("payment.Publish Pages")}
            used={usedPublishPages}
            limit={limitUsedPublishPages}
          />
        </Col>
        <Col>
          <UsedProgress
            title={t("payment.Share Projects / Pages")}
            used={
              data?.paymentPlans?.freePlan
                ? data?.paymentPlans.freePlan?.sharedProjects +
                  data?.paymentPlans.freePlan?.sharedPages
                : 0
            }
            limit={10}
          />
        </Col>
      </Row>
    </>
  );
}
